import { EditableNumberField } from "@client/components/round/allocator/TrancheListEditor/EditableNumberField";
import { PageTitle } from "@client/components/round/PageTitle";
import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import LoadingSpinner from "@client/components/utils/LoadingSpinner";
import SegmentedControl from "@client/components/utils/SegmentedControl";
import { DownloadFile } from "@client/utils/DownloadFile";
import { exportSpeculationSettlements } from "@client/utils/exportSpeculationSettlements";
import { formatMoney } from "@client/utils/formatMoney";
import { isUserRoundAdminOrFunder } from "@engine/round/accessors";
import floor from "lodash/floor";
import { useContext, useState } from "react";
import { DispatchContext, StateContext } from "./Round";
import {
  GrantRow,
  SpeculationGrantApplicationData,
  convertGrantAmountToNumber,
  useSpeculationGrantData,
} from "./useSpeculationGrantData";

export const SpeculationGrants = withErrorBoundary(() => {
  const dispatch = useContext(DispatchContext);
  const [rounded, setRounded] = useState(true);
  const loader = useSpeculationGrantData();

  const { round, actingAsUserId: userId } = useContext(StateContext);

  // TODO: multi funder
  const userIsRoundAdminOrFunder = isUserRoundAdminOrFunder(round, userId);

  if (loader.type === "loading") {
    return <LoadingSpinner />;
  }

  if (loader.type === "error") {
    return `Error: ${loader.error.message}`;
  }

  if (loader.type === "noFunder") {
    return (
      <div className="mx-auto p-4">
        <PageTitle title="Speculation Requests & Settlements" />
        <div className="p-4 bg-stone-300 dark:bg-stone-600 rounded">
          <p className="text-lg mb-2">No speculation grant funder found.</p>
          <p>
            This feature requires a funder with shortname "Jaan Tallinn" or "JT"
            to be present in the round. Please add a funder with one of these
            shortnames to enable speculation grant settlements.
          </p>
        </div>
      </div>
    );
  }

  const data = loader.data;

  async function setFunderSettlementBudget(
    speculationGrantSettlementBudget: number,
  ) {
    await dispatch({
      type: "FUNDER_SETTLEMENT_BUDGET_CHANGE",
      funderId: data.funderId,
      budget: speculationGrantSettlementBudget,
    });
  }

  async function handleSetZeroReturn() {
    await setFunderSettlementBudget(data.totalGrantAmount);
  }

  return (
    <div className="mx-auto p-4">
      <div className="w-[800px] h-[95vh] flex flex-col">
        <PageTitle title="Speculation Requests & Settlements" />
        <div className="ml-auto">
          <SegmentedControl
            label="Rounding:"
            selectedOption={rounded ? "$1K" : "$1"}
            setSelectedOption={(option) => setRounded(option === "$1K")}
            options={["$1", "$1K"]}
          />
        </div>
        <div className="flex justify-between p-2 items-baseline bg-stone-300 dark:bg-stone-600 mb-[3px]">
          <label>Speculation Budget: &nbsp;</label>

          <span>
            <EditableNumberField
              className="max-w-[80px] text-right mr-2"
              disabled={!userIsRoundAdminOrFunder}
              value={data.settlementBudget}
              isMoney={true}
              min={0}
              max={1.0e10}
              onSubmitted={(value) => setFunderSettlementBudget(value)}
            />
          </span>
          <span className="flex gap-[4px] items-center">
            <label>Price at: </label>
            <button
              className="p-2 rounded bg-stone-400 hover:bg-stone-500 dark:bg-stone-700 dark:hover:bg-stone-800"
              onClick={handleSetZeroReturn}
            >
              Zero Return
            </button>
          </span>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex gap-x-[2px] mb-[3px]">
            <div className="w-[391.5px] p-2 bg-stone-400 dark:bg-stone-700 font-bold">
              Grantee
            </div>
            <div className="grow p-2 bg-stone-400 dark:bg-stone-700 font-bold">
              Grant Amount
            </div>
            <div className="grow p-2 bg-stone-400 dark:bg-stone-700 font-bold">
              Settlement Price
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-4 gap-y-[3px] gap-x-[2px]">
              {Object.entries(data.applicationSpeculationGrantData).map(
                ([sffApplicationId, grantData]) => (
                  <SpeculationGrantItem
                    key={sffApplicationId}
                    grantData={grantData}
                    budget={data.settlementBudget}
                    rounded={rounded}
                  />
                ),
              )}
            </div>
          </div>
        </div>

        <div className="text-xl flex justify-center items-center hover:text-stone-300">
          <DownloadFile
            data={exportSpeculationSettlements(data)}
            filename="speculationSettlements.tsv"
            className="p-2 rounded bg-stone-400 hover:bg-stone-500 dark:bg-stone-700 dark:hover:bg-stone-800 mt-2"
            title="Export Speculation Settlements"
          />
        </div>
      </div>
    </div>
  );
});

function SpeculationGrantItem({
  grantData,
  budget,
  rounded,
}: {
  grantData: SpeculationGrantApplicationData;
  budget: number;
  rounded: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  let displayedAmountTotal = 0;
  let displayedUtilityTotal = 0;

  const formatValue = (value: number) => {
    if (rounded) {
      return formatMoney(value, {
        showDollarSign: true,
        showMagnitudeUnit: true,
      });
    } else {
      return "$" + value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  };

  const calculateTotal = (value: number) => {
    if (rounded) {
      return 1000 * Math.round(value / 1000);
    }
    return value;
  };

  const grantRows: GrantRow[] = [];
  for (const multiGrant of grantData.simultaneousGrants) {
    for (const grantRow of multiGrant.grants) {
      grantRows.push(grantRow);
    }
  }

  return (
    <>
      <div
        className="col-span-2 p-2 bg-stone-400 dark:bg-stone-600 cursor-pointer hover:opacity-90"
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <div className="font-semibold">{grantData.application.longname}</div>
        <div className="text-stone-700 dark:text-stone-300">
          {grantData.application.shortname}
        </div>

        <div
          className="line-clamp-3 box-orient-vertical text-ellipsis overflow-hidden my-2 text-xs"
          style={{
            WebkitLineClamp: expanded ? "initial" : floor(grantRows.length * 2),
          }}
        >
          {grantData.request["What you do (≤500 chars)"]}
          <br></br>
          <br></br>
          <b>Reason for expedition: </b>
          {grantData.request["Reason for expedition (≤500 chars)"]}
          <br></br>
          <br></br>
          <b>Request size: </b>
          {grantData.request["Speculation grant amount"]}
        </div>
      </div>

      <div className="col-span-2 h-full grid grid-rows-auto gap-y-[2px]">
        {grantRows.map((grantRow, idx) => {
          const amount = convertGrantAmountToNumber(
            grantRow["Speculator Amount"],
          );
          const utilityValue =
            grantRow.utility != null ? grantRow.utility * Number(budget) : 0;

          displayedAmountTotal += calculateTotal(amount);
          displayedUtilityTotal += calculateTotal(utilityValue);

          return (
            <div
              key={idx}
              className="grid grid-cols-2 bg-stone-300 dark:bg-stone-500"
            >
              <div className="p-2 flex items-center">{formatValue(amount)}</div>
              <div className="p-2 flex items-center">
                {formatValue(utilityValue)}
              </div>
            </div>
          );
        })}
        <div className="grid grid-cols-2 bg-stone-400 dark:bg-stone-700 font-bold">
          <div className="p-2 flex items-center">
            {formatValue(displayedAmountTotal)}
          </div>
          <div className="p-2 flex items-center">
            {formatValue(displayedUtilityTotal)}
          </div>
        </div>
      </div>
    </>
  );
}
